// ── Workflow preview strip ──────────────────────────────────────────────────
//
// Flattens a workflow into a colour-coded chip chain (MATCHLIST → KYC → IDV →
// KYC → … → AML), each chip a type badge + provider label + ruleset subtitle,
// chevron-separated — a compact, readable summary of what a workflow does.
import { useState } from 'react'
import { ChevronRight, ChevronDown } from 'lucide-react'
import type { Workflow, Task, StepTask } from '../types'
import { isBranchTask, isStepTask } from '../types'

const STEP_COLORS: Record<string, { badge: string; tint: string; text: string }> = {
  MATCHLIST: { badge: '#b91c1c', tint: 'rgba(220,38,38,0.12)', text: '#b91c1c' },
  KYC: { badge: '#16a34a', tint: 'rgba(22,163,74,0.12)', text: '#15803d' },
  IDV: { badge: '#2563eb', tint: 'rgba(37,99,235,0.12)', text: '#1d4ed8' },
  AML: { badge: '#7c3aed', tint: 'rgba(124,58,237,0.12)', text: '#7c3aed' },
  KYB: { badge: '#0d9488', tint: 'rgba(13,148,136,0.12)', text: '#0f766e' },
}

// Friendly labels for known provider ids; anything else is humanised generically.
const PROVIDER_LABELS: Record<string, string> = {
  dvs: 'DVS',
  manual: 'Manual',
  equifax: 'Equifax',
  superchoice: 'Superchoice',
  'experian-cdh-v2': 'Experian-CDH-v2',
  experian: 'Experian',
  refinitiv: 'Refinitiv',
  complyadvantage: 'ComplyAdvantage',
  incode: 'Incode',
  daon: 'Daon',
  onfido: 'Onfido',
}

function humanize(v: string): string {
  const known = PROVIDER_LABELS[v.toLowerCase()]
  if (known) return known
  if (/^[a-z0-9]{2,3}$/i.test(v)) return v.toUpperCase()
  return v
    .split(/[-_]/)
    .map((p) => (p.length <= 2 ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1)))
    .join('-')
}

interface Chip {
  type: keyof typeof STEP_COLORS
  label: string
  subtitle?: string
}

function stepChip(s: StepTask): Chip | null {
  const name = s.configuration.name
  const p = s.configuration.parameters ?? {}
  const provider = typeof p.providerName === 'string' ? p.providerName : undefined
  const ruleset = typeof p.rulesetName === 'string' ? p.rulesetName : undefined
  const amlName = typeof p.name === 'string' ? p.name : undefined
  switch (name) {
    case 'MATCHLIST':
      return { type: 'MATCHLIST', label: 'Matchlist' }
    case 'KYC':
      return { type: 'KYC', label: provider ? humanize(provider) : 'KYC', subtitle: ruleset }
    case 'IDV':
      return { type: 'IDV', label: provider ? humanize(provider) : 'IDV' }
    case 'AML':
      return { type: 'AML', label: provider ? humanize(provider) : amlName ? humanize(amlName) : 'AML' }
    case 'KYB':
      return { type: 'KYB', label: provider ? humanize(provider) : 'KYB' }
    default:
      return null // DECISION / TOGGLEMONITORING aren't external checks — omit from the strip
  }
}

// DFS collect the external-check chips in tree order.
function flatten(tasks: Task[]): Chip[] {
  const out: Chip[] = []
  const walk = (ts: Task[]) => {
    for (const t of ts) {
      if (isStepTask(t)) {
        const chip = stepChip(t)
        if (chip) out.push(chip)
      } else if (isBranchTask(t)) {
        t.configuration.branches.forEach((b) => walk(b.tasks))
      }
    }
  }
  walk(tasks)
  return out
}

function StepChip({ chip }: { chip: Chip }) {
  const c = STEP_COLORS[chip.type]
  return (
    <div className="inline-flex items-stretch rounded-md overflow-hidden shrink-0" style={{ border: `1px solid ${c.tint}` }}>
      <span className="flex items-center px-2 text-[10px] font-bold text-white" style={{ background: c.badge }}>
        {chip.type}
      </span>
      <span className="flex flex-col justify-center px-2 py-1" style={{ background: c.tint }}>
        <span className="text-xs font-medium leading-tight" style={{ color: c.text }}>
          {chip.label}
        </span>
        {chip.subtitle && <span className="text-[9px] leading-tight text-[#64748b]">{chip.subtitle}</span>}
      </span>
    </div>
  )
}

export function WorkflowPreviewStrip({ workflow }: { workflow: Workflow }) {
  const [showKyc, setShowKyc] = useState(false)
  const chips = flatten(workflow.tasks)
  const kycChips = chips.filter((c) => c.type === 'KYC')

  return (
    <div className="w-full">
      <div className="flex items-baseline gap-2 min-w-0 mb-3">
        <span className="text-sm font-semibold text-[#1e293b] truncate">{workflow.name}</span>
        <span className="text-[10px] font-mono text-[#64748b] shrink-0">v{workflow.version}</span>
      </div>

      {chips.length === 0 ? (
        <p className="text-[11px] text-[#64748b]">No external checks configured yet.</p>
      ) : (
        <div className="flex items-center flex-wrap gap-1.5">
          {chips.map((chip, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <StepChip chip={chip} />
              {i < chips.length - 1 && <ChevronRight size={13} className="text-[#64748b] shrink-0" />}
            </div>
          ))}
        </div>
      )}

      {kycChips.length > 0 && (
        <div className="mt-3">
          <button
            onClick={(e) => { e.stopPropagation(); setShowKyc((s) => !s) }}
            className="flex items-center gap-1 text-[11px] text-[#64748b] hover:text-[#1e293b]"
          >
            {showKyc ? <ChevronDown size={12} /> : <ChevronRight size={12} />} KYC requirements
          </button>
          {showKyc && (
            <div className="mt-1.5 flex flex-col gap-1 pl-4">
              {kycChips.map((c, i) => (
                <div key={i} className="text-[11px] text-[#475569]">
                  <span style={{ color: STEP_COLORS.KYC.text }}>{c.label}</span>
                  {c.subtitle && <span className="text-[#64748b]"> — {c.subtitle}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
